import { router } from "@inertiajs/react"
import { AlertCircle, Loader2 } from "lucide-react"
import { useState } from "react"

import { LiveTestCall } from "@/components/agents/setup/live-test-call"
import {
  PhoneNumberPicker,
  numberChoiceComplete,
  useNumberChoices,
} from "@/components/agents/setup/phone-number-picker"
import { areaCodeFromPhone } from "@/components/agents/setup/receptionist/phone-utils"
import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import type { SetupStepProps } from "@/components/agents/setup/types"
import { FormField } from "@/components/form-field"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { agentPath, setupAgentPath } from "@/routes"

// IMP-199: errors returned from the Retell sub-steps (provision/sync/
// activate/phone) used to land in fieldErrors but the wizard never
// rendered any of them — Chris's bug report was "the page reloads and
// nothing happens" because a 4xx from Retell was silently swallowed.
const LAUNCH_ERROR_KEYS = [
  "retell_provision",
  "retell_sync",
  "retell_activate",
  "retell_phone",
] as const

// Keys must match Agents::Receptionist::SetupValidator on the server.
// We always submit phone_option="new" — IMP-224 removed the in-wizard
// "Forward calls" path. Call-forwarding is handled post-setup since it
// requires carrier-side configuration we can't run inside the wizard.
const K_OPTION = "phone_option"
const K_AREA_CODE = "preferred_area_code"
// IMP-538: the number picked from the list, bought exactly at Launch.
const K_SELECTED_NUMBER = "selected_phone_number"

function readString(data: Record<string, unknown>, key: string): string {
  const v = data[key]
  return typeof v === "string" ? v : ""
}

function formatAreaCode(value: string): string {
  return value.replace(/\D/g, "").slice(0, 3)
}

export function ConnectStep({
  agent,
  stepNumber,
  totalSteps,
  businessPhone,
}: SetupStepProps) {
  // Pre-population precedence:
  //   1. Whatever the user already saved on this step (resume mid-flow).
  //   2. Area code derived from the business profile phone (IMP-224).
  //   3. Empty — they can still launch; ProvisionPhoneNumber falls back
  //      to "any available US number".
  const [areaCode, setAreaCode] = useState<string>(() => {
    const saved = readString(agent.setup_data, K_AREA_CODE)
    if (saved) return saved
    return areaCodeFromPhone(businessPhone)
  })
  const [selectedNumber, setSelectedNumber] = useState<string>(() =>
    readString(agent.setup_data, K_SELECTED_NUMBER),
  )
  const [processing, setProcessing] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [numbersRefresh, setNumbersRefresh] = useState(0)
  const { choices, retry } = useNumberChoices(
    agent.uid,
    areaCode,
    numbersRefresh,
  )

  function changeAreaCode(value: string) {
    const next = formatAreaCode(value)
    // A number picked for another area code no longer applies.
    if (next !== areaCode) setSelectedNumber("")
    setAreaCode(next)
  }

  function submit({ complete }: { complete: boolean }) {
    setProcessing(true)
    router.patch(
      setupAgentPath({ id: agent.uid }),
      {
        setup_step: complete ? "step-connect" : "step-transfers",
        complete: complete,
        setup_data: {
          // Always "new" — the in-wizard fork was removed in IMP-224.
          // AgentsController#update_setup gates ProvisionPhoneNumber on
          // this value, so keeping it set preserves the existing
          // launch-time provisioning wiring.
          [K_OPTION]: "new",
          [K_AREA_CODE]: areaCode,
          [K_SELECTED_NUMBER]: selectedNumber,
        },
      },
      {
        preserveScroll: true,
        onError: (errors) => {
          setFieldErrors(errors as Record<string, string>)
          // The picked number may have been taken since the list loaded:
          // offer a fresh list to pick from.
          if ("retell_phone" in errors) {
            setSelectedNumber("")
            setNumbersRefresh((n) => n + 1)
          }
        },
        onFinish: () => setProcessing(false),
        onSuccess: () => {
          if (complete) {
            router.visit(agentPath({ id: agent.uid }))
          }
        },
      },
    )
  }

  // Area code is optional: blank ⇒ ProvisionPhoneNumber picks any
  // available US number. We block on a partial entry (1 or 2 digits) since
  // the backend rejects those, and, once numbers are listed, on picking one.
  const canFinish =
    (areaCode === "" || /^\d{3}$/.test(areaCode)) &&
    numberChoiceComplete(choices, selectedNumber)

  return (
    <WizardLayout
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => submit({ complete: false })}
            disabled={processing}
          >
            Back
          </Button>
          <Button
            onClick={() => submit({ complete: true })}
            disabled={processing || !canFinish}
          >
            {processing && <Loader2 className="size-4 animate-spin" />}
            {processing ? "Launching…" : "Launch"}
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Test and go live"
        description="Talk to me in your browser to hear how I answer. When you launch, I get my own number, and you can forward your business line to it anytime."
      />

      {LAUNCH_ERROR_KEYS.some((k) => fieldErrors[k]) && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Couldn&rsquo;t launch your receptionist</AlertTitle>
          <AlertDescription>
            <ul className="list-disc space-y-1 pl-4">
              {LAUNCH_ERROR_KEYS.map((k) =>
                fieldErrors[k] ? <li key={k}>{fieldErrors[k]}</li> : null,
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* IMP-391: the receptionist always provisions its own dedicated
          number — no more reusing another agent's (IMP-361 reversed). */}
      <FormField
        label="Preferred area code"
        htmlFor="area_code"
        error={fieldErrors[K_AREA_CODE]}
      >
        <Input
          id="area_code"
          inputMode="numeric"
          maxLength={3}
          value={areaCode}
          onChange={(e) => changeAreaCode(e.target.value)}
        />
        {/* IMP-538: pick the actual number, rather than being assigned one. */}
        <div className="mt-3">
          <PhoneNumberPicker
            areaCode={areaCode}
            choices={choices}
            selected={selectedNumber}
            onSelect={setSelectedNumber}
            onRetry={retry}
          />
        </div>
      </FormField>

      <LiveTestCall
        agentUid={agent.uid}
        title="Live test call"
        idleDescription="Use your browser microphone to talk to me."
        speakerLabel="Assistant"
        waitingMessage="Waiting for the conversation to start…"
      />
    </WizardLayout>
  )
}
